// Support Service - Mock implementation for frontend development
// In a real application, this would connect to a backend API

export interface SupportTicket {
  id: string;
  subject: string;
  category: string;
  status: 'open' | 'in-progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  createdAt: string;
  lastUpdated: string;
  messages: {
    sender: 'user' | 'support';
    message: string;
    timestamp: string;
  }[];
}

export interface TicketFormData {
  subject: string;
  category: string;
  description: string;
  priority: 'low' | 'medium' | 'high';
}

// Mock data for support tickets
const mockTickets: SupportTicket[] = [
  {
    id: 'T-1023',
    subject: 'Package not showing in my account',
    category: 'packages',
    status: 'open',
    priority: 'medium',
    createdAt: '2025-03-25T14:32:00',
    lastUpdated: '2025-03-25T14:32:00',
    messages: [
      {
        sender: 'user',
        message: "'I received a notification that my package arrived, but it's not showing up in my account.The tracking number is USPS12345678.'",
        timestamp: '2025-03-25T14:32:00'
      }
    ]
  },
  {
    id: 'T-1019',
    subject: 'Question about consolidation fees',
    category: 'billing',
    status: 'in-progress',
    priority: 'low',
    createdAt: '2025-03-22T10:15:00',
    lastUpdated: '2025-03-23T11:42:00',
    messages: [
      {
        sender: 'user',
        message: "'I'd like to know more about the consolidation fees.The pricing page doesn't explain how it's calculated.'",
        timestamp: '2025-03-22T10:15:00'
      },
      {
        sender: 'support',
        message: "'Thank you for your inquiry. Consolidation fees are calculated based on the number of packages being consolidated and their total volume. For your specific case, could you please let me know how many packages you're looking to consolidate?'",
        timestamp: '2025-03-23T11:42:00'
      }
    ]
  },
  {
    id: 'T-1008',
    subject: 'Shipping delayed to my country',
    category: 'shipping',
    status: 'resolved',
    priority: 'high',
    createdAt: '2025-03-18T09:23:00',
    lastUpdated: '2025-03-21T16:07:00',
    messages: [
      {
        sender: 'user',
        message: 'My shipment has been stuck for 5 days with no updates. Shipment ID: SH-2345.',
        timestamp: '2025-03-18T09:23:00'
      },
      {
        sender: 'support',
        message: "'I apologize for the delay. Weve checked with the carrier and there's a temporary hold on shipments to your region due to weather conditions. We're monitoring the situation.'",
        timestamp: '2025-03-19T11:10:00'
      },
      {
        sender: 'user',
        message: 'Thanks for checking. Please keep me updated on any changes.',
        timestamp: '2025-03-19T15:43:00'
      },
      {
        sender: 'support',
        message: 'Good news! The shipping restrictions have been lifted, and your package is now moving again. You should see tracking updates within 24 hours.',
        timestamp: '2025-03-21T16:07:00'
      }
    ]
  }
];

// Support ticket categories
export const ticketCategories = [
  { value: 'account', label: 'Account Issues' },
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'packages', label: 'Package Reception' },
  { value: 'shipping', label: 'Shipping Problems' },
  { value: 'consolidation', label: 'Consolidation Issues' },
  { value: 'other', label: 'Other' }
];

// Mock API functions
export const supportService = {
  // Get all tickets for the current user
  getTickets: (): Promise<SupportTicket[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(mockTickets);
      }, 500);
    });
  },

  // Get a specific ticket by ID
  getTicketById: (id: string): Promise<SupportTicket | undefined> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const ticket = mockTickets.find(t => t.id === id);
        resolve(ticket);
      }, 300);
    });
  },

  // Create a new support ticket
  createTicket: (ticketData: TicketFormData): Promise<SupportTicket> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const newTicket: SupportTicket = {
          id: `T-${1000 + mockTickets.length + 1}`,
          subject: ticketData.subject,
          category: ticketData.category,
          status: 'open',
          priority: ticketData.priority,
          createdAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          messages: [
            {
              sender: 'user',
              message: ticketData.description,
              timestamp: new Date().toISOString()
            }
          ]
        };

        // In a real app, this would be persisted to a database
        mockTickets.push(newTicket);
        resolve(newTicket);
      }, 700);
    });
  },

  // Add a reply to an existing ticket
  addReply: (ticketId: string, message: string): Promise<SupportTicket | undefined> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        const ticketIndex = mockTickets.findIndex(t => t.id === ticketId);
        if (ticketIndex === -1) {
          resolve(undefined);
          return;
        }

        const ticket = { ...mockTickets[ticketIndex] };
        ticket.messages.push({
          sender: 'user',
          message,
          timestamp: new Date().toISOString()
        });
        ticket.lastUpdated = new Date().toISOString();

        mockTickets[ticketIndex] = ticket;
        resolve(ticket);
      }, 500);
    });
  },  // Get frequently asked questions
  getFAQs: (): Promise<{ question: string; answer: string }[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([
          // General Category
          {
            question: "How do I start using OTL Express?",
            answer: "To begin, sign up for an account on our platform. Once registered and logged in, you'll be assigned a unique address and ID to use when placing online orders."
          },
          {
            question: "What is the unique address provided for?",
            answer: "This address is your personal delivery point at our OTL Warehouse. Use this address when ordering from online stores or retailers. Your packages will be sent to this location for further processing."
          },
          {
            question: "What happens after my item arrives at the warehouse?",
            answer: "Once we receive your parcel at the OTL Warehouse, our admin team will log the item into the system. You'll see the received items listed in your account dashboard."
          },
          {
            question: "Can I combine multiple items into one shipment?",
            answer: "Yes! You can select multiple received items and request to combine them. The system will process your request and notify our admin team to package them together."
          },
          {
            question: "What happens to the combined items?",
            answer: "After combining, a new shipment entry with updated values (such as combined weight and size) will appear in your dashboard. The original individual items will be removed."
          },

          // How It Works Category
          {
            question: "How do I ship a combined package?",
            answer: "Once your combined shipment appears, select it and choose the 'Ship' option. You'll be shown different shipping methods and prices. Pick your preferred one to proceed."
          },
          {
            question: "What happens after I choose a shipping method?",
            answer: "Your shipment will be marked as Processing. Our admin team will be notified to initiate the shipping process, including generating tracking numbers and updating the system."
          },
          {
            question: "How can I track my shipment?",
            answer: "Once the admin completes the shipping process, tracking details will be added to your shipment. You'll see your package under the 'Shipped' section with real-time updates."
          },
          {
            question: "Can I track multiple shipments separately?",
            answer: "Yes. Each shipment you create—whether combined or individual—will have its own tracking number and history, all visible in your account under the 'Shipped' section."
          },
          {
            question: "Who can I contact for help with my parcel or shipment?",
            answer: "You can reach out to our support team via the Help section in your dashboard for any assistance regarding parcel arrivals, combining, shipping, or tracking issues."
          },

          // Additional FAQs - Shipping & Tracking
          {
            question: "How long does shipping take?",
            answer: "Shipping times vary depending on your location and the shipping method you choose. Economy shipping typically takes 10-20 business days, standard shipping takes 5-10 business days, and express shipping takes 2-5 business days."
          },
          {
            question: "What shipping carriers do you use?",
            answer: "We partner with major international carriers including DHL, FedEx, UPS, and local postal services to provide you with the best shipping options. The available carriers will depend on your destination country and package specifications."
          },
          {
            question: "What happens if my package is lost or damaged?",
            answer: "All shipments include basic insurance coverage. If your package is lost or damaged during transit, please contact our customer service within 7 days of the expected delivery date to file a claim. Additional insurance options are available for high-value items."
          },
          {
            question: "Can I choose my preferred shipping method?",
            answer: "Yes, when you request shipping for your packages, you'll be presented with all available shipping methods for your destination, along with estimated delivery times and costs. You can choose the option that best meets your needs for speed and budget."
          },

          // Packages & Storage
          {
            question: "How are my packages identified at the warehouse?",
            answer: "Each customer is assigned a unique ID that must be included in the shipping address. Our warehouse team uses this ID to identify your packages when they arrive, ensuring they're properly logged in the system and associated with your account."
          },
          {
            question: "What fees are involved?",
            answer: "Our service includes a subscription fee for your virtual address. Additional fees include shipping costs (based on weight, dimensions, and destination), optional package consolidation fees, and storage fees for packages kept longer than 30 days."
          },
          {
            question: "How long can you store my packages?",
            answer: "We offer 30 days of free storage for all packages. After that period, a storage fee of $1 per day per package applies. We recommend consolidating and shipping your packages within the free storage period to avoid additional fees."
          },
          {
            question: "Do you provide package photos?",
            answer: "Yes, we take photos of all packages upon arrival at our warehouse. Basic exterior photos are included free of charge. If you need additional detailed photos of the package contents, this service is available for a small fee per photo."
          },
          {
            question: "What happens to my original packages after consolidation?",
            answer: "When packages are consolidated, we carefully combine the items into a new shipment with optimal packaging to minimize shipping costs. The original packaging is typically discarded unless you specifically request otherwise. The new consolidated shipment will appear in your dashboard, and the original separate items will be removed."
          },

          // Billing & Payment
          {
            question: "What payment methods do you accept?",
            answer: "We accept major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers. All payments are processed securely through our payment gateway."
          },
          {
            question: "How are shipping costs calculated?",
            answer: "Shipping costs are calculated based on the dimensional weight or actual weight of your package (whichever is greater), the destination country, and the shipping method you select. You can get an estimate using our shipping calculator before requesting shipment."
          },
          {
            question: "Do I have to pay customs fees?",
            answer: "Customs duties and taxes are not included in our shipping rates and are the responsibility of the recipient. These charges vary by country and are determined by customs authorities based on the value and type of items being imported."
          },
          {
            question: "When do I pay for shipping?",
            answer: "You pay for shipping when you select a shipping method for your package or consolidated shipment. Payment must be completed before we process your shipment for international delivery."
          },
          {
            question: "Are there any hidden fees?",
            answer: "We pride ourselves on transparency. All our fees are clearly displayed before you confirm any service. You'll always see a detailed breakdown of costs including subscription fees, shipping charges, and any add-on services you select."
          },
          {
            question: "What are the benefits of consolidation?",
            answer: "Consolidation combines multiple packages into a single shipment, reducing shipping costs significantly as you'll pay for one larger package instead of multiple smaller ones. This often results in substantial savings on international shipping costs, especially for multiple small packages."
          },
          {
            question: "Are there prohibited items?",
            answer: "Yes, we cannot forward certain prohibited items including but not limited to: dangerous goods, hazardous materials, perishables, illegal items, and extremely high-value items without insurance."
          }
        ]);
      }, 300);
    });
  },

  // Get knowledge base articles
  getKnowledgeBaseArticles: (): Promise<{ title: string; content: string }[]> => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve([
          {
            title: "Complete Guide to OTL Express Service Flow",
            content: "Step 1: Sign up to the system to get your unique address with a personal ID.\nStep 2: Use this address when shopping at online sites or retailers.\nStep 3: The retailer ships your orders to the OTL Warehouse.\nStep 4: Once received, our admin team logs your parcel into the system.\nStep 5: You'll see the received items in your dashboard.\nStep 6: You can select multiple items and request to combine them.\nStep 7: System processes your request and notifies admin to package them together.\nStep 8: A new shipment with updated values appears in your dashboard (original items removed).\nStep 9: Select the shipment and choose the 'Ship' option.\nStep 10: Choose from shipping methods and prices displayed.\nStep 11: Your shipment is marked as Processing and admin is notified.\nStep 12: Admin initiates shipping process, including tracking updates.\nStep 13: Your package appears in the 'Shipped' section with real-time tracking."
          },
          {
            title: "How to Use Your Unique OTL Address",
            content: "Your unique OTL Express address is displayed prominently on your Dashboard after signing in. When shopping online, enter this complete address including your personal ID as the shipping address during checkout. This personal ID is crucial as it helps our warehouse team identify your packages when they arrive. All packages sent to this address will be received at our warehouse, logged in the system by our admin team, and appear in your dashboard under 'Received Items'."
          },
          {
            title: "Package Consolidation Process",
            content: "To combine multiple packages into one shipment:\n1) Go to your dashboard and view your received items\n2) Select multiple items by checking the boxes next to each package you want to combine\n3) Click the 'Combine' button\n4) The system will process your request and notify our admin team\n5) The admin team will package the items together\n6) A new shipment entry with updated values (weight, size, etc.) will appear in your dashboard\n7) The original individual items will be removed from your view\n8) The new consolidated shipment is ready for shipping"
          },
          {
            title: "Shipping Your Packages",
            content: "When you're ready to ship a package or consolidated shipment:\n1) Select the shipment from your dashboard\n2) Click the 'Ship' button\n3) Browse through the different shipping methods and prices shown\n4) Select your preferred shipping option\n5) Your shipment will be marked as 'Processing'\n6) Our admin team will be notified to start the shipping process\n7) The admin will create an order and update tracking details\n8) Your package will appear in the 'Shipped' section of your dashboard\n9) You can track your shipment in real-time with the provided tracking information"
          },
          {
            title: "Tracking Your Shipments",
            content: "OTL Express provides comprehensive tracking for all your shipments. Once the admin completes the shipping process and updates the tracking details in the system, you'll find your package under the 'Shipped' section with real-time updates. Each shipment you create—whether combined or individual—has its own tracking number and history, all visible in your account. This makes it easy to monitor multiple packages simultaneously and know exactly where each one is in the delivery process."
          },
          {
            title: "Package Storage and Handling",
            content: "All packages received at our warehouse are processed with care. When your parcels arrive, our admin team logs them into the system, and you'll receive a notification. Your items will be visible in your dashboard, where you can decide to ship them individually or combine multiple packages. Our consolidation service helps reduce shipping costs by combining multiple items into a single shipment. After combining, a new shipment entry will appear in your dashboard with updated values, and the original separate items will be removed."
          }
        ]);
      }, 400);
    });
  }
};

export default supportService;
